import React from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { PermitStatus, PermitApplicationStatus } from "common/types/permits";

export interface PermitStatusAlertProps {
  permitStatus: PermitStatus;
  applicationStatus: PermitApplicationStatus;
  reviewComment?: string | null;
  expiryDate?: string | null;
  issuingAuthority: string;
  onStartNewApplication: () => void;
}

export const PermitStatusAlert: React.FC<PermitStatusAlertProps> = ({
  permitStatus,
  applicationStatus,
  reviewComment,
  expiryDate,
  issuingAuthority,
  onStartNewApplication,
}) => {
  // Active permits don't show an alert
  if (permitStatus === "active" && applicationStatus === "approved") {
    return null;
  }

  // Pending review
  if (applicationStatus === "pending") {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Awaiting City Review</AlertTitle>
        <Typography variant="body2">
          Your permit application is currently under review by {issuingAuthority}. You will be notified once a decision
          has been made.
        </Typography>
      </Alert>
    );
  }

  // Awaiting additional info
  if (applicationStatus === "awaiting_additional_info") {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Additional Information Required</AlertTitle>
        {reviewComment && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {reviewComment}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Document upload functionality will be available soon.
        </Typography>
      </Alert>
    );
  }

  // Denied
  if (applicationStatus === "denied" || permitStatus === "denied") {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <AlertTitle>Application Denied</AlertTitle>
        {reviewComment && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {`Reason: ${reviewComment}`}
          </Typography>
        )}
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onStartNewApplication}>
            Start New Application
          </Button>
        </Box>
      </Alert>
    );
  }

  // Expired
  if (permitStatus === "expired") {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Permit Expired</AlertTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This permit expired on {expiryDate ? new Date(expiryDate).toLocaleDateString() : "N/A"}. Apply for a new
          permit to continue operating.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onStartNewApplication}>
            Apply for New Permit
          </Button>
        </Box>
      </Alert>
    );
  }

  // Suspended
  if (permitStatus === "suspended" || permitStatus === "pending_suspension") {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <AlertTitle>{permitStatus === "suspended" ? "Permit Suspended" : "Pending Suspension"}</AlertTitle>
        <Typography variant="body2">
          Please contact {issuingAuthority} for more information about your permit status.
        </Typography>
      </Alert>
    );
  }

  return null;
};

export default PermitStatusAlert;
